import { Controller } from "stimulus"
import { trackGoogleEvent, trackMixpanelEvent, trackPostHogEvent } from '../../src/javascripts/tracking'

// A Stimulus controller to send events based on data attributes
export default class extends Controller {
  static values = {
    name: String,
    properties: Object,
    trigger: String,
    // Optional sessionStorage key: the event fires at most once per key in
    // this browser session, so Turbo re-renders and reloads do not refire it.
    onceKey: String,
    // Send to PostHog only, never to the GTM dataLayer or Mixpanel. Journey
    // events carry the order key, which must not reach GTM.
    posthogOnly: Boolean,
    // Extra properties for PostHog alone (e.g. order_id on begin_checkout),
    // so the dataLayer and Mixpanel payloads stay as they were.
    posthogProperties: Object
  }

  connect() {
    if (!this.isValid()) return

    if (this.triggerValue == "onload") {
      return this.handleEvent()  // Trigger immediately on connect
    }

    // Bind the handler once for correct removal later
    this.boundHandler = this.handleEvent.bind(this)
    this.element.addEventListener(this.triggerValue, this.boundHandler)
  }

  disconnect() {
    if (this.boundHandler) {
      this.element.removeEventListener(this.triggerValue, this.boundHandler)
    }
  }

  // Validates required values
  isValid() {
    if (!this.nameValue || !this.triggerValue) {
      console.warn(
        "[Track Event Controller] Missing required values (name or trigger):",
        this.element
      )
      return false
    }
    return true
  }

  // Claims the once-key for this browser session. Never claims it while
  // analytics consent is absent, so a refused render cannot suppress the event
  // for a later consented one. If storage is unavailable the event still fires.
  claimOnce() {
    if (!this.onceKeyValue) return true

    const consent = window.wgPosthogConsent
    if (consent && typeof consent.allowed === "function" && !consent.allowed()) return false

    try {
      if (window.sessionStorage.getItem(this.onceKeyValue)) return false
      window.sessionStorage.setItem(this.onceKeyValue, "1")
    } catch (error) {
      // sessionStorage blocked: fall through and fire without the dedupe
    }
    return true
  }

  // Same order as trackEvent: PostHog, Mixpanel, then the GTM dataLayer.
  send() {
    trackPostHogEvent(this.nameValue, { ...this.propertiesValue, ...this.posthogPropertiesValue })
    if (this.posthogOnlyValue) return

    trackMixpanelEvent(this.nameValue, this.propertiesValue, {})
    trackGoogleEvent(this.nameValue, this.propertiesValue)
  }

  // Event handler for triggering event capture
  handleEvent(event) {
    if (!this.claimOnce()) return

    try {
      // If link opens in new tab, prevent default and delay navigation
      if (this.element.target === '_blank' && this.triggerValue === 'click') {
        event.preventDefault()

        // Open blank window immediately (preserves user gesture)
        const newWindow = window.open('', '_blank')

        this.send()

        // Navigate the new window after tracking
        setTimeout(() => {
          if (newWindow) {
            newWindow.location.href = this.element.href
          }
        }, 100)
      } else {
        this.send()
      }
      console.log(`[Track Event] captured: ${this.nameValue}`)
    } catch (error) {
      console.error("[Track Event] Failed to capture:", error)
    }
  }
}
